import { FC } from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useTranslations from '../global/translation/hooks/translation';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../global/ThemeContext';
import { designTokens } from '../../theme/designTokens';

const Pricing: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;

  type LanguageKeys = keyof typeof translations.label.price;
  const typedCurrentLanguage = currentLanguage as LanguageKeys;

  const exampleVals = {
    price: translations.label?.price?.[typedCurrentLanguage] || 'N/A',
    trainingIndywidulane: translations.label?.individualTraining?.[typedCurrentLanguage] || 'N/A',
    trainingWParze: translations.label?.pairedTraining?.[typedCurrentLanguage] || 'N/A',
    trainingNaKoniuPrywatny: translations.label?.privateHorseTraining?.[typedCurrentLanguage] || 'N/A',
    lonza: translations.label?.lunge?.[typedCurrentLanguage] || 'N/A',
    teren: translations.label?.trail?.[typedCurrentLanguage] || 'N/A',
    serwis: translations.label?.service?.[typedCurrentLanguage] || 'N/A',
    pensjonat: translations.label?.pension?.[typedCurrentLanguage] || 'N/A',
    trainingSkokowyIndywidulane: translations.label?.individualJumpTraining?.[typedCurrentLanguage] || 'N/A',
    ustalanyIndywidualnie: translations.label?.individuallyDetermined?.[typedCurrentLanguage] || 'N/A',
    passDiscount: translations.label?.passDiscount?.[typedCurrentLanguage] || 'N/A',
  };

  const pricingTiers = [
    {
      title: translations.label.pricingPensionTitle[currentLanguage],
      description: translations.label.pricingPensionDesc[currentLanguage],
      priceLabel: translations.label.pricingPensionFrom[currentLanguage],
      price: exampleVals.ustalanyIndywidualnie,
      cta: translations.label.pricingPensionCTA[currentLanguage],
      features: [
        translations.label.pricingFeature1[currentLanguage],
        translations.label.pricingFeature2[currentLanguage],
        translations.label.pricingFeature3[currentLanguage],
        translations.label.pricingFeature4[currentLanguage],
      ],
      isPrimary: true,
    },
    {
      title: translations.label.pricingTrainingTitle[currentLanguage],
      description: translations.label.pricingTrainingDesc[currentLanguage],
      priceLabel: translations.label.pricingTrainingFrom[currentLanguage],
      price: '120 zł',
      cta: translations.label.pricingTrainingCTA[currentLanguage],
      features: [
        translations.label.pricingFeature5[currentLanguage],
        translations.label.pricingFeature6[currentLanguage],
        translations.label.pricingFeature7[currentLanguage],
        translations.label.pricingFeature8[currentLanguage],
      ],
      isPrimary: false,
    },
  ];

  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        bgcolor: darkMode ? palette.background.elevated : 'transparent',
        py: isMobile ? 6 : 12,
        px: isMobile ? 2 : 4,
      }}
    >
      {/* Header */}
      <Grid item xs={12} mb={2}>
        <Typography
          variant="h4"
          fontWeight={300}
          sx={{
            color: darkMode ? '#FFFFFF' : palette.primary,
            textAlign: 'center',
            letterSpacing: '-0.5px',
          }}
        >
          {translations.menuItems.pricing[typedCurrentLanguage]}
        </Typography>
      </Grid>

      {/* Intro Text */}
      <Grid item xs={12} mb={6}>
        <Typography
          variant="body2"
          sx={{
            color: darkMode ? '#CCCCCC' : '#555555',
            textAlign: 'center',
            fontSize: '15px',
          }}
        >
          {translations.label.pricingIntro[currentLanguage]}
        </Typography>
      </Grid>

      {/* Pricing Cards */}
      <Grid container spacing={isMobile ? 3 : 4} sx={{ maxWidth: '1000px', mx: 'auto', mb: 6 }}>
        {pricingTiers.map((tier, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: tier.isPrimary ? palette.accent : darkMode ? palette.border.default : 'rgba(20, 56, 125,0.12)',
                borderRadius: '16px',
                bgcolor: darkMode ? palette.background.default : '#FFFFFF',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: darkMode ? '0 12px 24px rgba(0, 0, 0, 0.4)' : '0 18px 38px rgba(20, 56, 125,0.12)',
                },
              }}
            >
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Tier Title */}
                <Typography
                  variant="h5"
                  fontWeight={400}
                  sx={{
                    color: tier.isPrimary ? (darkMode ? palette.accent : palette.primary) : darkMode ? '#FFFFFF' : palette.text.primary,
                    mb: 1,
                    letterSpacing: '-0.3px',
                  }}
                >
                  {tier.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: darkMode ? '#CCCCCC' : '#555555',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  {tier.description}
                </Typography>

                {/* Price */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: darkMode ? '#888888' : '#999999',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontSize: '11px',
                    }}
                  >
                    {tier.priceLabel}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={300}
                    sx={{
                      color: darkMode ? '#FFFFFF' : '#000000',
                      mt: 0.5,
                    }}
                  >
                    {tier.price}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3, borderColor: darkMode ? '#333333' : '#EEEEEE' }} />

                {/* Features */}
                <Box sx={{ flexGrow: 1, mb: 3 }}>
                  {tier.features.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: '18px',
                          mr: 1.5,
                          mt: 0.3,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: darkMode ? '#CCCCCC' : '#555555',
                          lineHeight: 1.6,
                          fontSize: '14px',
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Button */}
                <Button
                  variant={tier.isPrimary ? 'contained' : 'outlined'}
                  fullWidth
                  sx={{
                    mt: 'auto',
                    py: 1.5,
                    fontSize: '15px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '10px',
                    ...(tier.isPrimary
                      ? {
                          bgcolor: theme.palette.primary.main,
                          color: '#FFFFFF',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-2px)',
                          },
                        }
                      : {
                          borderColor: darkMode ? '#555555' : '#CCCCCC',
                          color: darkMode ? '#FFFFFF' : '#000000',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          },
                        }),
                  }}
                  onClick={() => {
                    if (tier.isPrimary) {
                      window.open('tel:729883891', '_self');
                      return;
                    }

                    window.location.href = 'https://lubie.urbanequestrian.pl/';
                  }}
                >
                  {tier.cta}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detailed Pricing Table */}
      <Grid item xs={12} sx={{ maxWidth: '800px', mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            fontWeight={400}
            sx={{
              color: darkMode ? '#FFFFFF' : '#000000',
              textAlign: 'center',
              mb: 1,
            }}
          >
            {translations.label.pricingDetailsTitle[currentLanguage]}
          </Typography>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            bgcolor: darkMode ? palette.background.default : '#FFFFFF',
            border: '1px solid',
            borderColor: darkMode ? palette.border.default : 'rgba(20, 56, 125,0.12)',
            borderRadius: '16px',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ color: darkMode ? '#CCCCCC' : '#555555', borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`, py: 2 }}>
                  {exampleVals.trainingIndywidulane}
                </TableCell>
                <TableCell
                  sx={{
                    color: darkMode ? '#FFFFFF' : '#000000',
                    borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`,
                    textAlign: 'right',
                    fontWeight: 500,
                  }}
                >
                  170 zł
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: darkMode ? '#CCCCCC' : '#555555', borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`, py: 2 }}>
                  {exampleVals.trainingWParze}
                </TableCell>
                <TableCell
                  sx={{
                    color: darkMode ? '#FFFFFF' : '#000000',
                    borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`,
                    textAlign: 'right',
                    fontWeight: 500,
                  }}
                >
                  120 zł
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: darkMode ? '#CCCCCC' : '#555555', borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`, py: 2 }}>
                  {exampleVals.trainingNaKoniuPrywatny}
                </TableCell>
                <TableCell
                  sx={{
                    color: darkMode ? '#FFFFFF' : '#000000',
                    borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`,
                    textAlign: 'right',
                    fontWeight: 500,
                  }}
                >
                  150 zł
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: darkMode ? '#CCCCCC' : '#555555', borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`, py: 2 }}>
                  {exampleVals.lonza} (30min)
                </TableCell>
                <TableCell
                  sx={{
                    color: darkMode ? '#FFFFFF' : '#000000',
                    borderBottom: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`,
                    textAlign: 'right',
                    fontWeight: 500,
                  }}
                >
                  100 zł
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: darkMode ? '#CCCCCC' : '#555555', border: 'none', py: 2 }}>{exampleVals.teren}</TableCell>
                <TableCell sx={{ color: darkMode ? '#FFFFFF' : '#000000', border: 'none', textAlign: 'right', fontWeight: 500 }}>150 zł</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pass Discount Note */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: darkMode ? '#888888' : '#999999',
              fontSize: '13px',
              lineHeight: 1.6,
              display: 'block',
            }}
          >
            {exampleVals.passDiscount}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Pricing;
